"use client";

import React from "react";
import Image from "next/image";
import { env } from "@/lib/environment";
import { Section } from "@/components/ui/Section";
import { Typography } from "@/components/ui";

const imgBaseUrl = `${env.NEXT_PUBLIC_CDN_URL}/images/press-release`;

interface PressRelease {
  id: string;
  title: string;
  description: string;
  link: string;
  image: string;
  logo: string;
  date: string;
  featured?: boolean;
}

interface PressReleaseListProps {
  pressReleases: PressRelease[];
}

const FeaturedPressCard = ({ item }: { item: PressRelease }) => (
  <a href={item.link} target="_blank" rel="noopener noreferrer" className="flex flex-col">
    <div className="relative overflow-hidden rounded-lg h-[60vh] md:h-auto">
      <Image
        src={`${imgBaseUrl}${item.image}`}
        alt={item.title}
        title={item.title}
        width={600}
        height={600}
        priority
        fetchPriority="high"
        className="w-full h-full object-cover"
      />
      <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent">
        <div className="flex items-center justify-between gap-3 mb-3">
          <Image
            src={`${imgBaseUrl}${item.logo}`}
            alt="Press logo"
            title="Press logo"
            width={100}
            height={80}
            priority
            fetchPriority="high"
            className="w-32 h-auto object-contain"
          />
          <Typography variant="body" as="p" className="text-white m-0">
            {item.date}
          </Typography>
        </div>
        <Typography variant="h4" as="h3" className="text-white mt-2 mb-2 font-bold">
          {item.title}
        </Typography>
        <Typography variant="body" as="p" className="text-white/90">
          {item.description}
        </Typography>
      </div>
    </div>
  </a>
);

const PressCard = ({ item }: { item: PressRelease }) => (
  <a href={item.link} target="_blank" rel="noopener noreferrer" className="block hover:opacity-90 transition-opacity">
    <Image
      src={`${imgBaseUrl}${item.image}`}
      alt={item.title}
      title={item.title}
      width={400}
      height={400}
      className="w-full rounded-lg"
    />
    <div className="flex items-center justify-between gap-3 my-6">
      <Image
        src={`${imgBaseUrl}${item.logo}`}
        alt="Press logo"
        title="Press logo"
        width={100}
        height={80}
        className="w-auto h-7 object-contain"
      />
      <Typography variant="body" as="p" className="m-0 text-[14px]">
        {item.date}
      </Typography>
    </div>
    <Typography variant="h4" as="h3" className="mt-3 text-[17px] font-medium">
      {item.title}
    </Typography>
    <Typography variant="body" as="p" className="mt-2 text-gray-600 text-base">
      {item.description}
    </Typography>
  </a>
);

export default function PressReleaseList({ pressReleases }: PressReleaseListProps) {
  const featuredReleases = pressReleases.filter((item) => item.featured);
  const pastReleases = pressReleases.filter((item) => !item.featured);
  const [showAll, setShowAll] = React.useState(false);

  const visibleReleases = showAll ? pastReleases : pastReleases.slice(0, 6);

  return (
    <>
      {/* Top Stories Section */}
      <Section variant="default">
        <Typography variant="h2" as="h2" className="mb-8">
          Top Stories
        </Typography>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {featuredReleases.map((item) => (
            <FeaturedPressCard key={item.id} item={item} />
          ))}
        </div>
      </Section>

      {/* Past Press Releases Section */}
      <Section variant="default">
        <Typography variant="h2" as="h2" className="mb-8">
          Past Press Releases
        </Typography>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {visibleReleases.map((item) => (
            <PressCard key={item.id} item={item} />
          ))}
        </div>

        <div className="text-center mt-12">
          <button
            type="button"
            onClick={() => setShowAll(!showAll)}
            className="text-primary px-8 py-3 underline rounded-lg transition-colors"
          >
            {showAll ? "View Less" : "View More"}
          </button>
        </div>
      </Section>
    </>
  );
}

